"use client"

import type { ReactNode } from "react"

type SpinnerSize = "sm" | "md" | "lg"

function Spinner({
  size = "md",
  className = "",
  ariaLabel,
}: {
  size?: SpinnerSize
  className?: string
  ariaLabel?: string
}) {
  const dim = size === "sm" ? 16 : size === "lg" ? 36 : 24
  return (
    <div
      className={`border-2 border-[var(--redy-red)] border-t-transparent rounded-full animate-spin ${className}`}
      style={{ width: dim, height: dim }}
      role="status"
      aria-label={ariaLabel ?? "Loading"}
    />
  )
}

type SkeletonType = "rows" | "cards" | "kpi" | "block"
type SkeletonTheme = "light" | "dark"

// P-5 (audit 2026-05-28): added dark theme variant so POS / KDS / kiosk
// surfaces (which sit on top of a charcoal background) can use the same
// skeleton primitive without showing a near-white block on dark glass.
// Light variant unchanged for backwards compatibility.
const SKELETON_BG: Record<SkeletonTheme, string> = {
  light: "var(--redy-warm-white)",
  dark: "rgba(255,255,255,0.06)",
}

function Skeleton({
  type = "rows",
  count = 5,
  theme = "light",
  className = "",
}: {
  type?: SkeletonType
  count?: number
  theme?: SkeletonTheme
  className?: string
}) {
  const bg = SKELETON_BG[theme]

  if (type === "rows") {
    return (
      <div className={`space-y-2 ${className}`} aria-hidden>
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className="h-12 rounded-[10px] animate-pulse"
            style={{ background: bg }}
          />
        ))}
      </div>
    )
  }
  if (type === "cards") {
    return (
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 ${className}`}
        aria-hidden
      >
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className="h-32 rounded-[14px] animate-pulse"
            style={{ background: bg }}
          />
        ))}
      </div>
    )
  }
  if (type === "kpi") {
    return (
      <div
        className={`h-56 rounded-[20px] animate-pulse ${className}`}
        style={{ background: bg }}
        aria-hidden
      />
    )
  }
  // block
  return (
    <div
      className={`rounded-[14px] animate-pulse ${className}`}
      style={{ background: bg }}
      aria-hidden
    />
  )
}

export type LoadingStateProps = {
  label?: ReactNode
  size?: SpinnerSize
  className?: string
}

export function LoadingState({
  label,
  size = "md",
  className = "",
}: LoadingStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center py-12 gap-3 ${className}`}
    >
      <Spinner size={size} />
      {label && (
        <p className="text-[13px] text-[var(--redy-secondary)]">{label}</p>
      )}
    </div>
  )
}

LoadingState.Spinner = Spinner
LoadingState.Skeleton = Skeleton
